import React, { useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import "./Home.css";
import CredentialsCard from "../../../components/common/PL/Cards/CredentialsCard/CredentialsCard";
import GuardiaTable from "../../../components/common/PL/Table/GuardiaTable";
import GuardiaVehicleTable from "../../../components/common/PL/Table/GuardiaVehicleTable";
import PeopleInsideViewToggle, {
  usePeopleInsideView,
} from "../../../components/common/PL/Toggle/PeopleInsideViewToggle";
import PeopleFilters from "../../../components/common/PL/Filters/PeopleFilters";
import { usePeopleInside } from "../RegistroIngreso/hooks/usePeopleInside";
import { useVehiclesInside } from "../RegistroIngreso/hooks/useVehiclesInside";
import { useDelayedState } from "../../../hooks/useDelayedState";
import usePLScannerListener from "../hooks/usePLScannerListener";
import { IBlackUser, ISearchUser } from "../../../store/types/userTypes";
import { useStore } from "../../../hooks/useStore";
import { useRegistroIngresoSession } from "../RegistroIngreso/session/RegistroIngresoSessionContext";
import {
  processRegistroIngresoScan,
  finalizeBuildingMismatchScan,
  PendingPassScanData,
} from "../RegistroIngreso/utils/processScan";
import ScanLoadingOverlay from "../../../components/common/PL/ScanLoadingOverlay/ScanLoadingOverlay";
import DialogConfirmation from "../../../components/common/PL/Dialog/DialogConfirmation";
import BlacklistDialog from "../../../components/common/PL/Dialog/BlacklistDialog";

const Home = () => {
  const history = useHistory();
  const { view, setView } = usePeopleInsideView();
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [selectedAuthorizationTags, setSelectedAuthorizationTags] = useState<string[]>([]);
  const [selectedTransportCompanies, setSelectedTransportCompanies] = useState<string[]>([]);
  // Carrocería: `getVehiclesInside` no la filtra, así que la aplica la tabla sobre lo ya cargado.
  const [selectedVehicleType, setSelectedVehicleType] = useState("");
  // Lo que se tipea se muestra al toque, pero la búsqueda contra el backend espera a que el guardia
  // deje de escribir (mismo criterio que la pantalla de egresos).
  const [search, setSearch] = useState("");
  const { state: filterText, setState: setFilterText } = useDelayedState<string>("", setSearch);
  // Cada vista pide sólo su endpoint: togglear no dispara el fetch de la otra.
  const { peopleInside, isLoading, refetch: refetchPeopleInside } = usePeopleInside({
    search,
    companies: selectedCompanies,
    authorizationTags: selectedAuthorizationTags,
    transportCompanies: selectedTransportCompanies,
    enabled: view === "person",
  });
  const {
    vehiclesInside,
    isLoading: isLoadingVehicles,
    refetch: refetchVehiclesInside,
  } = useVehiclesInside({
    search,
    companies: selectedCompanies,
    transportCompanies: selectedTransportCompanies,
    enabled: view === "vehicle",
  });

  const unidadesSinChofer = vehiclesInside.filter((row) => !row.driverInside).length;
  const session = useRegistroIngresoSession();
  const { authStore } = useStore();
  const isScanningRef = useRef(false);
  const [isScanning, setIsScanning] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [buildingDialogOpen, setBuildingDialogOpen] = useState(false);
  const [pendingPassScanData, setPendingPassScanData] = useState<PendingPassScanData | null>(null);
  const [blockedData, setBlockedData] = useState<IBlackUser | null>(null);

  const handleScan = async (results: ISearchUser) => {
    if (isScanningRef.current) return;
    isScanningRef.current = true;
    setIsScanning(true);

    await processRegistroIngresoScan({
      results,
      mode: "entry",
      intent: "append-tab",
      session,
      authStore,
      history,
      onBuildingMismatch: (pending) => {
        setIsScanning(false);
        setPendingPassScanData(pending);
        setBuildingDialogOpen(true);
      },
      onError: (message) => {
        setIsScanning(false);
        setErrorMessage(message);
        setErrorDialogOpen(true);
        isScanningRef.current = false;
      },
      onFullyBlocked: (data) => {
        setIsScanning(false);
        setBlockedData(data);
        isScanningRef.current = false;
      },
    });
  };

  usePLScannerListener({ enabled: true, onScan: handleScan });

  const handleConfirmBuildingMismatch = async () => {
    setBuildingDialogOpen(false);
    if (pendingPassScanData) {
      await finalizeBuildingMismatchScan({
        pending: pendingPassScanData,
        mode: "entry",
        intent: "append-tab",
        session,
        history,
        onFullyBlocked: (data) => {
          setBlockedData(data);
          isScanningRef.current = false;
        },
      });
      setPendingPassScanData(null);
    }
  };

  const handleCancelBuildingMismatch = () => {
    setBuildingDialogOpen(false);
    setPendingPassScanData(null);
    isScanningRef.current = false;
  };

  const handleErrorDialogClose = () => {
    setErrorDialogOpen(false);
    isScanningRef.current = false;
  };

  return (
    <div className="container">
      <CredentialsCard />
      <PeopleFilters
        searchValue={filterText}
        onSearchChange={setFilterText}
        selectedCompanies={selectedCompanies}
        onCompaniesChange={setSelectedCompanies}
        selectedAuthorizationTags={selectedAuthorizationTags}
        onAuthorizationTagsChange={setSelectedAuthorizationTags}
        selectedTransportCompanies={selectedTransportCompanies}
        onTransportCompaniesChange={setSelectedTransportCompanies}
        vehicleTypeMode={view === "vehicle"}
        selectedVehicleType={selectedVehicleType}
        onVehicleTypeChange={setSelectedVehicleType}
        trailing={<PeopleInsideViewToggle value={view} onChange={setView} />}
      />
      {view === "vehicle" && !isLoadingVehicles && (
        <div className="unitsCount">
          {vehiclesInside.length}{" "}
          {vehiclesInside.length === 1 ? "unidad dentro" : "unidades dentro"} del complejo
          {unidadesSinChofer > 0 && <span> · {unidadesSinChofer} sin su chofer original</span>}
        </div>
      )}
      <div className="tableContainer">
        {view === "person" ? (
          <GuardiaTable
            rows={peopleInside}
            isLoading={isLoading}
            onDockUpdated={refetchPeopleInside}
          />
        ) : (
          <GuardiaVehicleTable
            rows={vehiclesInside}
            vehicleTypeFilter={selectedVehicleType}
            isLoading={isLoadingVehicles}
            onDockUpdated={refetchVehiclesInside}
          />
        )}
      </div>
      <ScanLoadingOverlay open={isScanning} />
      <DialogConfirmation
        open={errorDialogOpen}
        onClose={handleErrorDialogClose}
        onConfirm={handleErrorDialogClose}
        title="Error"
        message={errorMessage}
        confirmLabel="Aceptar"
        cancelLabel="Cancelar"
      />
      <DialogConfirmation
        open={buildingDialogOpen}
        onClose={handleCancelBuildingMismatch}
        onConfirm={handleConfirmBuildingMismatch}
        title="Advertencia"
        message="Esta autorización es para otro establecimiento"
        confirmLabel="Continuar"
        cancelLabel="Cancelar"
      />
      <BlacklistDialog
        open={!!blockedData}
        blacklistedData={blockedData}
        onReject={() => setBlockedData(null)}
        onAllow={() => setBlockedData(null)}
      />
    </div>
  );
};

export default Home;
